import { Coverage, VehicleCoverage } from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import ChevronDownIcon from '../../../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../../../assets/icons/ChevronUp';
import ExpandableList from '../../../../components/ExpandableList';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import useSortedCoverages, {
  NO_COVERAGE,
} from '../../../../hooks/useSortedCoverages';
import { handleForwardSlash, shouldShowCoverageText } from '../../helper';

interface CoverageDetailProps {
  data: VehicleCoverage;
  policyCoverages: Coverage[];
  policyNumber?: string;
}

const VehicleCoverageDetails: React.FC<CoverageDetailProps> = ({
  data: {
    year,
    make,
    series,
    vin,
    garagingZipCode,
    coverages,
    waivedLiabilityInd,
  },
  policyCoverages,
  policyNumber,
}: CoverageDetailProps) => {
  const { track } = useAnalytics();

  const sortedCoverages = useSortedCoverages(coverages);

  const getSpecialEquipmentCoverage = (
    item: Coverage,
    hasCoverage: boolean
  ): string | undefined => {
    const includedCoverage =
      item.coverageCd === 'SPECEQUIP' && item.coveragePremium === '$0.00';

    if (hasCoverage) {
      if (includedCoverage) {
        return 'Included';
      } else {
        return item.coveragePremium;
      }
    } else {
      return item.coverageLimit;
    }
  };

  const content = (isExpanded: boolean) => (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        p: 2,
        width: 1
      }}>
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          variant="h3"
          data-testid="autoCoverageTitle"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            color: colors.csaaBlack,
            letterSpacing: { mobile: 0.3, tablet: 0.4 },
          }}
        >
          {year} {make} {series}
        </Typography>
        {vin && (
          <Typography
            className="csaa-qm-encrypt"
            sx={{
              fontWeight: 400,
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
              color: colors.csaaMid,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              letterSpacing: { mobile: 0.3, tablet: 0.4 },
            }}
          >
            VIN: {vin}
          </Typography>
        )}
        <Typography
          className="csaa-qm-encrypt"
          sx={{
            fontWeight: 400,
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            color: colors.csaaMid,
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            letterSpacing: { mobile: 0.3, tablet: 0.4 },
          }}
        >
          Zip code: {garagingZipCode}
        </Typography>
      </Box>
      <Stack
        sx={{
          background: 'rgba(11, 20, 33, 0.1)',
          cursor: 'pointer',
          p: 0.5,
          height: 'fit-content',
          borderRadius: 1,
        }}
      >
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: colors.csaaBorder,
        borderRadius: 2,
      }}
    >
      <ExpandableList
        content={content}
        id={`vehicle-coverage-${vin}`}
        onClick={(isOpen) =>
          track('Vehicle Coverage Details Toggled', {
            event_type: 'UX Modified',
            location: 'Feature Card',
            link: `${year} ${make} ${series}`,
            selection: isOpen ? 'Closed' : 'Opened',
            policy_number: policyNumber,
          })
        }
      >
        <Stack
          data-testid="coverageDetailMainContainer"
          spacing={1}
          sx={{
            p: 2,
            backgroundColor: colors.csaaNeutralBackground,
            borderTop: '1px solid',
            borderColor: colors.csaaBorder,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          {waivedLiabilityInd && (
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                backgroundColor: colors.csaaWhite,
                p: { mobile: 2, tablet: 3 },
                boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
                borderRadius: 2
              }}>
              <Stack spacing={{ mobile: 2, tablet: 2.5 }} sx={{
                width: 0.7
              }}>
                {policyCoverages.map((policyCoverage, idx) => (
                  <Typography
                    key={`${idx}-${policyCoverage.coverageCd}`}
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: { mobile: '16px', tablet: '18px' },
                      color: colors.csaaBlack,
                    }}
                  >
                    {policyCoverage.coverageDescription}
                  </Typography>
                ))}
              </Stack>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: { mobile: '16px', tablet: '18px' },
                  color: colors.csaaBlack,
                }}
              >
                Waived
              </Typography>
            </Stack>
          )}
          {sortedCoverages.map((item, idx) => {
            const hasCoverage =
              item.coverageLimit?.toLowerCase() !== NO_COVERAGE;

            return (
              <Stack
                key={`${item.coverageCd}-${idx}`}
                sx={{
                  backgroundColor: colors.csaaWhite,
                  p: { mobile: 2, tablet: 3 },
                  boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" sx={{
                  justifyContent: "space-between"
                }}>
                  <Typography
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: { mobile: 16, tablet: 18 },
                      lineHeight: '24px',
                      fontWeight: 700,
                      letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      color: colors.csaaBlack,
                    }}
                  >
                    {item.coverageDescription}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: { mobile: 16, tablet: 18 },
                      lineHeight: '24px',
                      fontWeight: 700,
                      letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      whiteSpace: 'nowrap',
                      color: colors.csaaBlack,
                    }}
                  >
                    {getSpecialEquipmentCoverage(item, hasCoverage)}
                  </Typography>
                </Stack>
                {hasCoverage && shouldShowCoverageText(item.coverageLimit) && (
                  <Stack direction="row">
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontWeight: 400,
                        fontSize: { mobile: 16, tablet: 18 },
                        lineHeight: '24px',
                        color: colors.csaaMid,
                        letterSpacing: { mobile: 0.3, tablet: 0.4 },
                        mt: 1,
                      }}
                    >
                      {item.coverageLimit}
                    </Typography>
                  </Stack>
                )}
                {hasCoverage && shouldShowCoverageText(item.coverageType) && (
                  <Stack direction="row">
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontWeight: 400,
                        fontSize: { mobile: 14, tablet: 16 },
                        lineHeight: '24px',
                        color: colors.csaaMid,
                        letterSpacing: { mobile: 0.2, tablet: 0.3 },
                        mt: 1,
                      }}
                    >
                      {handleForwardSlash(item.coverageType!)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      </ExpandableList>
    </Box>
  );
};

export default VehicleCoverageDetails;
